import React, { FC } from 'react';
import styled from 'styled-components';

type Variant = 'info' | 'success' | 'warning' | 'error';

type Props = {
  text: string;
  icon: string;
  href?: string;
  type?: Variant;
  openInNewTab?: boolean;
};

const VARIANT_STYLES: Record<
  Variant,
  { bg: string; border: string; text: string }
> = {
  info: { bg: `#F2F8FF`, border: `#DCEBFF`, text: `#4B5563` },
  success: { bg: `#F3FAF5`, border: `#D8F0DE`, text: `#44524A` },
  warning: { bg: `#FFF8ED`, border: `#FFE9CC`, text: `#5A4C3A` },
  error: { bg: `#FFF5F5`, border: `#FFD7D7`, text: `#5A3C3C` },
};

export const Alert: FC<Props> = ({
  type = `info`,
  text,
  icon,
  href,
  openInNewTab = true,
}) => {
  const s = VARIANT_STYLES[type];

  const handleClick = () => {
    if (!href) return;
    if (openInNewTab) {
      window.open(href, `_blank`, `noopener,noreferrer`);
    } else {
      window.location.href = href;
    }
  };

  return (
    <Wrapper
      bg={s.bg}
      border={s.border}
      clickable={!!href}
      onClick={handleClick}
    >
      <Icon src={icon} alt="alert-icon" />
      <Text color={s.text}>{text}</Text>
    </Wrapper>
  );
};

const Wrapper = styled.div<{ bg: string; border: string; clickable: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: ${(p) => p.bg};
  border: 1px solid ${(p) => p.border};
  text-decoration: none;
  width: fit-content;
  cursor: ${(p) => (p.clickable ? `pointer` : `default`)};
  transition: background 140ms ease, transform 140ms ease;

  &:active {
    transform: translateY(1px);
  }
`;

const Icon = styled.img`
  width: 16px;
  height: 16px;
  opacity: 0.8;
  flex-shrink: 0;
`;

const Text = styled.span<{ color: string }>`
  color: ${(p) => p.color};
  font-size: 12px;
`;
